import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
  TextField,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Avatar,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const AUTH_TOKEN = "7633d6c7-1111-4fd9-b78c-14e0312957f8";

// Styled TextField for modern design
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiInputBase-root": {
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #E0E0E0",
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
  },
}));

const FamilyDialog = ({ open, onClose, onSelect }) => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addFamilyMode, setAddFamilyMode] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "",
    age: "",
    relation: "",
    gender: "",
    phone_number: "",
    note: "",
  });

  // Fetch family members on dialog open
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetch(`${BASE_URL}/family-members`, {
        method: "GET",
        headers: {
          Authorization: AUTH_TOKEN,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            setFamilyMembers(data.data);
          } else {
            setError("Failed to fetch family members.");
          }
        })
        .catch(() => setError("An error occurred while fetching family members."))
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Handle adding a new family member
  const handleAddMember = () => {
    setLoading(true);
    fetch(`${BASE_URL}/family-members`, {
      method: "POST",
      headers: {
        Authorization: AUTH_TOKEN,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newMember),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) {
          setFamilyMembers((prev) => [...prev, data.data]); // Add new member to the list
          setAddFamilyMode(false);
          setNewMember({
            name: "",
            age: "",
            relation: "",
            gender: "",
            phone_number: "",
            note: "",
          });
        } else {
          setError("Failed to add family member.");
        }
      })
      .catch(() => setError("An error occurred while adding the family member."))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        {addFamilyMode ? "Add Family Member" : "Select Family Member"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" textAlign="center" mt={2}>
            {error}
          </Typography>
        ) : addFamilyMode ? (
          <Box>
            <Typography variant="subtitle1" mb={2}>
              Enter family member details below:
            </Typography>
            <StyledTextField
              label="Name"
              fullWidth
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
            />
            <StyledTextField
              label="Age"
              fullWidth
              value={newMember.age}
              onChange={(e) => setNewMember({ ...newMember, age: e.target.value })}
            />
            <StyledTextField
              label="Relation"
              fullWidth
              value={newMember.relation}
              onChange={(e) => setNewMember({ ...newMember, relation: e.target.value })}
            />
            <StyledTextField
              label="Gender"
              fullWidth
              value={newMember.gender}
              onChange={(e) => setNewMember({ ...newMember, gender: e.target.value })}
            />
            <StyledTextField
              label="Phone Number"
              fullWidth
              value={newMember.phone_number}
              onChange={(e) => setNewMember({ ...newMember, phone_number: e.target.value })}
            />
            <StyledTextField
              label="Note"
              fullWidth
              value={newMember.note}
              onChange={(e) => setNewMember({ ...newMember, note: e.target.value })}
            />
          </Box>
        ) : (
          <List>
            {familyMembers.map((member) => (
              <ListItem
                key={member.reference_id}
                button
                onClick={() => {
                  onSelect(member);
                  onClose();
                }}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  marginBottom: 1,
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    backgroundColor: "#F5F5F5",
                  },
                }}
              >
                <Avatar
                  sx={{ bgcolor: "#3f51b5", marginRight: 2 }}
                >
                  {member.name.charAt(0).toUpperCase()}
                </Avatar>
                <ListItemText
                  primary={`${member.name} (${member.relation})`}
                  secondary={`Age: ${member.age}, Gender: ${member.gender}`}
                />
              </ListItem>
            ))}
            {familyMembers.length === 0 && (
              <Typography color="text.secondary" textAlign="center" mt={2}>
                No family members found. Add a new member.
              </Typography>
            )}
          </List>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        {addFamilyMode ? (
          <>
            <Button onClick={() => setAddFamilyMode(false)}>Cancel</Button>
            <Button onClick={handleAddMember} variant="contained" color="primary">
              Save
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddFamilyMode(true)}
              variant="contained"
              color="primary"
            >
              Add Family Member
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FamilyDialog;